import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { dateToDateString } from '@utils/date';

import {
  GetTherapiesRequestParams,
  TherapiesSortBy,
  Therapy,
} from '@interfaces/therapies.interface';

import {
  useCountTherapiesByPatientIdQuery,
  useGetTherapiesByPatientIdQuery,
} from '@api/patients.api';
import {
  useCountTherapiesQuery,
  useGetManyTherapiesQuery,
} from '@api/therapies.api';

interface TherapiesTableProps {
  patientId?: string;
}

export default function TherapiesTable({ patientId }: TherapiesTableProps) {
  // ==========================================================================
  // General
  // ==========================================================================
  const navigate = useNavigate();

  const [filters, setFilters] = useState<GetTherapiesRequestParams>({
    page: +1,
    pageLength: +10,
    sortBy: 'id',
    sortOrder: 'asc',
  });

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<Therapy>>({
    columnAccessor: 'id',
    direction: 'asc',
  });

  useEffect(() => {
    // Skip the initial mount
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as TherapiesSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================
  const {
    data: therapiesCountGeneral = { count: 0 },
    isLoading: isLoadingCountGeneral,
  } = useCountTherapiesQuery(undefined, { skip: !!patientId });

  const { data: dataGeneral = [], isLoading: isLoadingGeneral } =
    useGetManyTherapiesQuery(
      {
        ...filters,
      },
      { skip: !!patientId },
    );

  const {
    data: therapiesCountPatient = { count: 0 },
    isLoading: isLoadingCountPatient,
  } = useCountTherapiesByPatientIdQuery(patientId!, { skip: !patientId });

  const { data: dataPatient = [], isLoading: isLoadingPatient } =
    useGetTherapiesByPatientIdQuery(
      {
        patientId: patientId!,
        params: filters,
      },
      { skip: !patientId },
    );

  const therapiesCount = useMemo(() => {
    if (!patientId) return therapiesCountGeneral;
    return therapiesCountPatient;
  }, [patientId, therapiesCountGeneral, therapiesCountPatient]);

  const data = useMemo(() => {
    if (!patientId) return dataGeneral;
    return dataPatient;
  }, [patientId, dataGeneral, dataPatient]);

  const isLoadingCount = useMemo(() => {
    if (!patientId) return isLoadingCountGeneral;
    return isLoadingCountPatient;
  }, [patientId, isLoadingCountGeneral, isLoadingCountPatient]);

  const isLoading = useMemo(() => {
    if (!patientId) return isLoadingGeneral;
    return isLoadingPatient;
  }, [patientId, isLoadingGeneral, isLoadingPatient]);

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <>
      <DataTable
        /* General */
        striped
        highlightOnHover
        noRecordsText="Nessuna terapia trovata"
        minHeight={data.length === 0 ? 200 : undefined}
        /* Data */
        fetching={isLoading || isLoadingCount}
        columns={[
          {
            accessor: 'description',
            title: 'Descrizione',
          },
          {
            accessor: 'startDate',
            title: 'Data di inizio',
            sortable: true,
            render: (record) => {
              return dateToDateString(new Date(record.startDate));
            },
          },
          {
            accessor: 'endDate',
            title: 'Data di fine',
            sortable: true,
            render: (record) => {
              return record.endDate
                ? dateToDateString(new Date(record.endDate))
                : '-';
            },
          },
        ]}
        records={data}
        onRowClick={({ record }) => {
          navigate(
            `/terapie/${record.id}${patientId ? `?patientId=${patientId}` : ''}`,
          );
        }}
        /* Sorting */
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        /* Pagination */
        page={filters.page!}
        recordsPerPage={filters.pageLength!}
        totalRecords={therapiesCount.count}
        paginationText={() => ''}
        recordsPerPageLabel=""
        recordsPerPageOptions={[10, 20, 50]}
        onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
        onRecordsPerPageChange={(newPageLength) =>
          setFilters({ ...filters, pageLength: newPageLength, page: 1 })
        }
      />
    </>
  );
}
